/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS205: Consider reworking code to avoid use of IIFEs
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import * as CrisprConstants from 'constants/CRISPRConstants';
import FolderSelector from 'file-browser/FolderSelector';
import FileStates, {SequenceFileState} from 'FileStates';
import CRISPRAnalysisStore from 'molbio/crispr/CRISPRAnalysisStore';
import CRISPRDefaults from 'molbio/crispr/CRISPRDefaults';
import HRTemplateActions from 'molbio/crispr/hr-template/HRTemplateActions';
import HRTemplateTabState from 'molbio/crispr/hr-template/HRTemplateTabState';
import SettingsForm, {Settings} from 'molbio/crispr/hr-template/SettingsForm';
import * as SequenceDAO from 'molbio/dao/SequenceDAO';
import notifications from 'notifications';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';
import {Optional} from 'util/Types';

const {HR_TEMPLATE_CREATE_TYPE, HR_TEMPLATE_STEP} = CrisprConstants;

export interface HRTemplateModalProps {
  onRequestClose: () => void;
  sequence: {
    id: string;
    name: string;
    activeVersionId: number;
    folderId: Optional<string>;
  };
  useCurrentSequence?: boolean;
  initialGenome?: string | null;
}

interface State {
  loading: boolean;
  createType: CrisprConstants.HRTemplateCreateType;
  settings: Settings;
  selectedFolderId: Optional<string>;
}

class HRTemplateModal extends React.Component<HRTemplateModalProps, State> {
  static defaultProps = {
    useCurrentSequence: false,
  };

  constructor(props: HRTemplateModalProps) {
    super(props);
    const {analysisLoading} = CRISPRAnalysisStore.getState();
    const analyses = CRISPRAnalysisStore.getAnalysesForSequence(props.sequence.id);

    // Sets a default value to clone in the case the CRISPRAnalysisStore
    // is still loading analyses.
    let createType = HR_TEMPLATE_CREATE_TYPE.CLONE;
    if ((!analysisLoading && _.isEmpty(analyses)) || props.useCurrentSequence) {
      createType = HR_TEMPLATE_CREATE_TYPE.MODIFY;
    }

    this.state = {
      loading: false,
      createType,
      settings: {
        pam: CRISPRDefaults.get('pam'),
        genome: props.initialGenome || CRISPRDefaults.get(CRISPRDefaults.KEYS.GENOME),
      },
      selectedFolderId: props.sequence.folderId,
    };
  }

  updateCreateType = (e: InputChangeEvent): void => {
    this.setState({createType: e.target.value as CrisprConstants.HRTemplateCreateType});
  };

  handleChangeSettings = (value: Settings): void => {
    this.setState({settings: value});
  };

  handleCreateSequence = async (): Promise<void> => {
    let promise: JQueryPromise<{id: string}>;
    if (this.state.createType === HR_TEMPLATE_CREATE_TYPE.CLONE) {
      try {
        assert(this.state.selectedFolderId != null, 'Folder ID is required for cloning');
        const cloneResponse = await SequenceDAO.cloneVersion(
          this.props.sequence.id,
          this.props.sequence.activeVersionId,
          this.state.selectedFolderId,
          `${this.props.sequence.name} (HR template)`
        );
        promise = FileStates.get()
          .openFolderItem(cloneResponse.id, {open: true, source: 'hr-template-design'})
          .then((fileState) => ({id: cloneResponse.id}));
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        notifications.notifyError(e);
        return;
      }
    } else {
      assert(this.state.createType === HR_TEMPLATE_CREATE_TYPE.MODIFY, 'Unexpected create type');
      promise = $.Deferred().resolve({id: this.props.sequence.id}).promise() as JQueryPromise<{id: string}>;
    }

    promise.then(({id}: {id: string}) => {
      // TODO(T4374): do not pass the whole backbone model into the HR Template actions and store
      const sequenceFile = FileStates.get().get(id) as SequenceFileState;
      const sequence = sequenceFile.model();

      // Create HR Template Design before opening the tab
      const savedVersions = _.object([[HR_TEMPLATE_STEP.INTRODUCE_EDITS, sequence.getActiveVersionId()]]);
      HRTemplateActions.create(sequence, {
        sequence: sequence.id,
        pam: this.state.settings.pam,
        genome: this.state.settings.genome,
        guide: '',
        step: HR_TEMPLATE_STEP.INTRODUCE_EDITS,
        armRegion: {
          start: null,
          end: null,
        },
        savedVersions,
      });

      sequenceFile.buffers().addTab(new HRTemplateTabState({sequence}));
      analytics.track('hr_template', 'began_hr_template_design');
    });
  };

  submit = async (): Promise<void> => {
    this.setState({loading: true});
    await withLoadingScreen(this.handleCreateSequence());
    this.props.onRequestClose();
    this.setState({loading: false});
  };

  renderCreateTypeInput = (type: CrisprConstants.HRTemplateCreateType, text: string): JSX.Element => (
    <div className="radio">
      <label>
        <input
          type="radio"
          name="design-type"
          value={type}
          checked={this.state.createType === type}
          onChange={this.updateCreateType}
        />
        {text}
      </label>
    </div>
  );

  override render(): JSX.Element {
    return (
      <Modal
        enableRedesign={false}
        title="Design HR template"
        onRequestSubmit={this.submit}
        onRequestClose={this.props.onRequestClose}
        buttons={[
          {
            text: 'Cancel',
            cancel: true,
            disabled: this.state.loading,
          },
          {
            text: this.state.loading ? 'Creating...' : 'Create',
            submit: true,
            disabled:
              this.state.loading ||
              (this.state.createType === HR_TEMPLATE_CREATE_TYPE.CLONE &&
                this.state.selectedFolderId == null),
          },
        ]}
      >
        <SettingsForm settings={this.state.settings} onChange={this.handleChangeSettings} />
        {!this.props.useCurrentSequence ? (
          <FormGroup>
            <Row>
              <Col className="text-right" sm={3}>
                {null}
              </Col>
              <Col sm={8}>
                {this.renderCreateTypeInput(HR_TEMPLATE_CREATE_TYPE.CLONE, 'Create a copy of this sequence')}
                {this.renderCreateTypeInput(HR_TEMPLATE_CREATE_TYPE.MODIFY, 'Modify this sequence')}
              </Col>
            </Row>
            {this.state.createType === HR_TEMPLATE_CREATE_TYPE.CLONE && (
              <Row className={css(styles.folderRow)}>
                <FieldLabel label="Folder" isRequired={true} />
                <FolderSelector
                  allowNone={false}
                  value={this.state.selectedFolderId}
                  onChange={(e) => {
                    this.setState({selectedFolderId: e.target.value});
                  }}
                />
              </Row>
            )}
          </FormGroup>
        ) : (
          <Row>
            <Col className="col-md-offset-2" md={10}>
              <p>The HR template will be created by modifying the imported sequence.</p>
            </Col>
          </Row>
        )}
      </Modal>
    );
  }
}

export default HRTemplateModal;

const styles = StyleSheet.create({
  folderRow: {
    margin: '12px',
  },
});
